// ErrorBoundary — stops a single page's render error from blanking the whole
// app. Without one, any thrown error in a route (e.g. the Focus query-key
// cache-shape crash, 2026-06) unmounts the entire tree → a blank screen that
// only a refresh recovers. Wrapped around the routed page content (keyed by
// pathname so it auto-resets on navigation), it shows a recoverable fallback
// while keeping the sidebar/layout intact.

import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { error: Error | null };

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for now; a later iteration can ship this to
    // OpenTelemetry/Honeycomb alongside the worker traces.
    console.error('Page error boundary caught:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="max-w-lg mx-auto mt-20 text-center px-6">
          <h2 className="text-lg font-semibold text-foreground">
            Something went wrong on this page
          </h2>
          <p className="mt-2 text-sm text-muted-foreground break-words">
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
